import React, { useMemo, useState } from 'react'
import { Search, Check, Plus, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { FIELD_SUGGESTIONS } from '../types'
import type { CrmObjectType } from '../types'

type Props = {
  objectType: CrmObjectType | null
  label: string
  deny: string[]
  onChange: (deny: string[]) => void
  onClose: () => void
}

const BlockFieldDialog: React.FC<Props> = ({ objectType, label, deny, onChange, onClose }) => {
  const { currentTheme: t } = useTheme()
  const [query, setQuery] = useState('')

  const suggestions = objectType ? FIELD_SUGGESTIONS[objectType] : []
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return suggestions
    return suggestions.filter((s) => s.label.toLowerCase().includes(q) || s.key.toLowerCase().includes(q))
  }, [suggestions, query])

  const isBlocked = (key: string) => deny.includes(key)

  const toggle = (key: string) => {
    onChange(isBlocked(key) ? deny.filter((f) => f !== key) : [...deny, key])
  }

  const normalizedQuery = query.trim().toLowerCase()
  const exactSuggestionMatch = suggestions.some((s) => s.key.toLowerCase() === normalizedQuery)
  const canAddCustom = normalizedQuery.length > 0 && !exactSuggestionMatch && !isBlocked(normalizedQuery)

  const addCustom = () => {
    if (!canAddCustom) return
    onChange([...deny, normalizedQuery])
    setQuery('')
  }

  return (
    <Dialog open={objectType !== null} onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent
        className="p-0 rounded-2xl shadow-xl max-w-md w-[92vw] border-none flex flex-col max-h-[85vh]"
        style={{ background: t.secondary, color: t.text }}
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-base font-semibold" style={{ color: t.text }}>
            Block fields on {label}
          </DialogTitle>
          <p className="text-[12.5px] mt-1" style={{ color: t.mutedText }}>
            Zeph will never write to a blocked field — even if directly asked to.
          </p>
        </DialogHeader>

        <div className="px-6 pt-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: t.mutedText }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canAddCustom) { e.preventDefault(); addCustom() } }}
              placeholder="Search fields, or type a custom property name"
              className="w-full h-9 rounded-lg pl-8 pr-3 text-[13px] outline-none"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}`, color: t.text }}
            />
          </div>
        </div>

        <div className="px-6 pt-3 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
          <div className="rounded-[12px] overflow-hidden" style={{ border: `1px solid ${t.border}` }}>
            {filtered.length === 0 && !canAddCustom && (
              <div className="py-6 text-center text-[12.5px]" style={{ color: t.mutedText }}>
                No matching fields
              </div>
            )}
            {filtered.map((s, i) => {
              const blocked = isBlocked(s.key)
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggle(s.key)}
                  className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left transition-colors"
                  style={{
                    borderTop: i === 0 ? 'none' : `1px solid ${t.borderSecondary}`,
                    backgroundColor: blocked ? t.errorBackground : 'transparent',
                  }}
                >
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium truncate" style={{ color: blocked ? t.errorText : t.text }}>
                      {s.label}
                    </span>
                    <span className="block text-[11px] font-mono truncate" style={{ color: blocked ? t.errorText : t.mutedText }}>
                      {s.key}
                    </span>
                  </span>
                  {blocked
                    ? <Check size={16} style={{ color: t.errorText }} />
                    : <Plus size={16} style={{ color: t.mutedText }} />}
                </button>
              )
            })}
            {canAddCustom && (
              <button
                type="button"
                onClick={addCustom}
                className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left transition-colors"
                style={{ borderTop: filtered.length > 0 ? `1px solid ${t.borderSecondary}` : 'none' }}
              >
                <Plus size={16} style={{ color: t.accent }} />
                <span className="text-[13px] font-medium" style={{ color: t.accent }}>
                  Block custom property "{query.trim()}"
                </span>
              </button>
            )}
          </div>
        </div>

        {deny.length > 0 && (
          <div className="px-6 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] mb-2" style={{ color: t.mutedText }}>
              Currently blocked ({deny.length})
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto custom-scrollbar">
              {deny.map((field) => (
                <span
                  key={field}
                  className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded text-[11px] font-mono"
                  style={{ backgroundColor: t.hoverBackground, color: t.text }}
                >
                  {field}
                  <button type="button" onClick={() => onChange(deny.filter((f) => f !== field))} style={{ color: t.mutedText }}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="px-6 pb-6 pt-4">
          <Button onClick={onClose} className="w-full sm:w-auto rounded-lg" style={{ background: t.accentGradient, color: t.buttonText }}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default React.memo(BlockFieldDialog)
